import React, { useState, useEffect } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import { Table, Button, Space, Modal, Card } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import ClassroomModal from './ClassroomModal'; // Keep original modal
import ConfirmationModal from './ConfirmationModal'; // Keep original confirmation modal

function EditDeleteClassroom() {
    const { t } = useTranslation();
    const [classrooms, setClassrooms] = useState([]);
    const [showClassroomModal, setShowClassroomModal] = useState(false);
    const [currentClassroom, setCurrentClassroom] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmationMessage, setConfirmationMessage] = useState('');
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const fetchClassrooms = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_URL}/classrooms`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setClassrooms(response.data);
            } catch (error) {
                console.error('Error fetching classrooms:', error);
                setError(t('fetchClassroomsError'));
            } finally {
                setLoading(false);
            }
        };

        fetchClassrooms();
    }, [API_URL, t]);

    const handleEdit = (classroom) => {
        setCurrentClassroom(classroom);
        setShowClassroomModal(true);
    };

    const handleDelete = (classroomId) => {
        Modal.confirm({
            title: t('confirmDelete'),
            content: t('areYouSureToDelete'),
            okText: t('yes'),
            cancelText: t('no'),
            onOk: async () => {
                try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`${API_URL}/classrooms/${classroomId}`, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    setClassrooms(prev => prev.filter(classroom => classroom.classroomid !== classroomId));
                    setConfirmationMessage(t('classroomDeleted'));
                    setShowConfirmationModal(true);
                } catch (error) {
                    console.error('Error deleting classroom:', error);
                    setError(t('deleteClassroomError'));
                }
            }
        });
    };

    const handleSaveClassroom = async (classroomData) => {
        try {
            const token = localStorage.getItem('token');
            let response;
            if (currentClassroom) {
                // Editing existing classroom
                response = await axios.put(`${API_URL}/classrooms/${currentClassroom.classroomid}`, classroomData, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setClassrooms(prev => prev.map(classroom => classroom.classroomid === response.data.classroomid ? response.data : classroom));
                setConfirmationMessage(t('classroomUpdated'));
            } else {
                // Creating new classroom
                response = await axios.post(`${API_URL}/classrooms`, classroomData, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setClassrooms(prev => [...prev, response.data]);
                setConfirmationMessage(t('classroomCreated'));
            }
            setShowClassroomModal(false);
            setShowConfirmationModal(true);
        } catch (error) {
            console.error('Error saving classroom:', error);
            setError(t('saveClassroomError'));
        }
    };

    const closeConfirmationModal = () => {
        setShowConfirmationModal(false);
    };

    const columns = [
        {
            title: t('number'),
            dataIndex: 'number',
            key: 'number',
        },
        {
            title: t('capacity'),
            dataIndex: 'capacity',
            key: 'capacity',
        },
        {
            title: t('availability'),
            dataIndex: 'availability',
            key: 'availability',
            render: (availability) => (
                availability ? t('available') : t('notAvailable')
            ),
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record.classroomid)}
                    />
                </Space>
            ),
        },
    ];

    if (loading) return <div className="ant-spin-container"><div className="ant-spin-spinning"><span className="ant-spin-dot"></span></div></div>;
    if (error) return <div className="ant-alert ant-alert-error">{error}</div>;

    return (
        <div>
            <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => { setCurrentClassroom(null); setShowClassroomModal(true); }}
                >
                    {t('createNewClassroom')}
                </Button>
        <Card style={{ marginTop: '16px'}}>
                
                {showClassroomModal && (
                    <ClassroomModal
                        isOpen={showClassroomModal}
                        onClose={() => setShowClassroomModal(false)}
                        onSave={handleSaveClassroom}
                        classroomData={currentClassroom}
                    />
                )}
                <ConfirmationModal
                    isOpen={showConfirmationModal}
                    message={confirmationMessage}
                    onClose={closeConfirmationModal}
                />
                <div className="task-list-container">
                    <Table
                        className="task-list-table"
                        columns={columns}
                        rowKey="classroomid"
                        pagination={false}
                        dataSource={[...classrooms].sort((a, b) => {
                            const numA = a.number.match(/\d+/g);
                            const numB = b.number.match(/\d+/g);
                
                            if (numA && numB) {
                                return parseInt(numA[0], 10) - parseInt(numB[0], 10); // Numeric sort if both have numbers
                            } else if (!numA && !numB) {
                                return a.number.localeCompare(b.number); // Alphabetical sort if both are letters
                            } else {
                                return numA ? -1 : 1; // Put numbers before letters
                            }
                        })}
                    />
                </div>
        </Card>
                </div>
    );
}

export default withRoleAccess(EditDeleteClassroom, ['admin', 'superadmin']);